import { useState, useEffect } from "react";
import { MessageCircle, Calendar, Smile, Frown, Meh, TrendingUp, Loader2, ChevronDown, ChevronRight, Bookmark } from "lucide-react";
import { cn } from "../lib/utils";
import { AppLayout } from "../components/AppLayout";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const MOOD_COLORS = {
  10: "#22C55E", 9: "#34D399", 8: "#6EE7B7", 7: "#86EFAC",
  6: "#BBF7D0", 5: "#FCD34D", 4: "#FBBF24", 3: "#F59E0B",
  2: "#EF4444", 1: "#DC2626",
};

const MODULE_LABELS = {
  friend: "Friend", psychologist: "Psychologist", mentor: "Mentor",
  social_guide: "Social", job_guardian: "Guardian", life_coach: "Coach",
  storyteller: "Storyteller", philosopher_king: "Philosopher King",
  creative_muse: "Creative Muse", executive_advisor: "Executive Advisor",
};

function MoodDot({ score }) {
  const color = MOOD_COLORS[score] || "#9CA3AF";
  return (
    <div
      className="w-5 h-5 rounded-full flex-shrink-0 border-2 border-white dark:border-gray-800 shadow-sm"
      style={{ backgroundColor: color }}
      title={`Mood: ${score}/10`}
    />
  );
}

export default function TimelinePage() {
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [expandedDays, setExpandedDays] = useState({});
  const token = localStorage.getItem("omni_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTimeline(); }, [days]);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sessions/timeline?days=${days}`, { headers });
      const data = await res.json();
      setTimeline(data.timeline || []);
      setStats(data.stats || {});
    } catch { /* silent */ }
    setLoading(false);
  };

  const toggleDay = (date) => {
    setExpandedDays(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.floor((today - d) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff < 7) return d.toLocaleDateString(undefined, { weekday: "long" });
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return "";
    try { return new Date(isoStr).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <AppLayout>
      <div className="p-4 max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold">Session Timeline</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Your journey with Omni</p>
          </div>
          <select
            data-testid="timeline-period-select"
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-xs bg-muted/50 border border-border/40 rounded-lg px-2.5 py-1.5 focus:outline-none"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>

        {/* Stats Bar */}
        {stats && !loading && (
          <div data-testid="timeline-stats" className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200/30 dark:border-blue-800/20 text-center">
              <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{stats.total_days_active}</p>
              <p className="text-[9px] text-blue-500/70 font-medium">Days Active</p>
            </div>
            <div className="p-2.5 rounded-xl bg-violet-50 dark:bg-violet-950/20 border border-violet-200/30 dark:border-violet-800/20 text-center">
              <p className="text-lg font-bold text-violet-600 dark:text-violet-400">{stats.total_sessions}</p>
              <p className="text-[9px] text-violet-500/70 font-medium">Sessions</p>
            </div>
            <div className="p-2.5 rounded-xl bg-pink-50 dark:bg-pink-950/20 border border-pink-200/30 dark:border-pink-800/20 text-center">
              <p className="text-lg font-bold text-pink-600 dark:text-pink-400">{stats.total_mood_entries}</p>
              <p className="text-[9px] text-pink-500/70 font-medium">Mood Logs</p>
            </div>
            <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200/30 dark:border-emerald-800/20 text-center">
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{stats.avg_mood_score ?? "—"}</p>
              <p className="text-[9px] text-emerald-500/70 font-medium">Avg Mood</p>
            </div>
          </div>
        )}

        {/* Timeline */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : timeline.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No activity in this period</p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-5 top-0 bottom-0 w-px bg-border/40" />

            <div className="space-y-1">
              {timeline.map(day => {
                const isExpanded = expandedDays[day.date] !== false;
                const avgDayMood = day.moods.length > 0 ? Math.round(day.moods.reduce((s, m) => s + m.score, 0) / day.moods.length) : null;
                return (
                  <div key={day.date} data-testid={`timeline-day-${day.date}`}>
                    {/* Day header */}
                    <button
                      onClick={() => toggleDay(day.date)}
                      className="w-full flex items-center gap-3 py-2 px-1 hover:bg-muted/30 rounded-lg transition-colors relative z-10"
                      data-testid={`timeline-toggle-${day.date}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-card border-2 border-border/50 flex items-center justify-center flex-shrink-0 shadow-sm">
                        {avgDayMood ? <MoodDot score={avgDayMood} /> : <Calendar className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-semibold">{formatDate(day.date)}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {day.sessions.length} session{day.sessions.length !== 1 ? "s" : ""}
                          {day.moods.length > 0 && ` · ${day.moods.length} mood log${day.moods.length !== 1 ? "s" : ""}`}
                          {day.saved_sessions.length > 0 && ` · ${day.saved_sessions.length} saved`}
                        </p>
                      </div>
                      {avgDayMood && <span className="text-[10px] font-bold mr-1" style={{ color: MOOD_COLORS[avgDayMood] }}>{avgDayMood}/10</span>}
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="ml-12 pl-2 border-l border-border/30 space-y-1.5 pb-2 animate-in fade-in slide-in-from-top-1 duration-150">
                        {/* Moods for this day */}
                        {day.moods.map((m, i) => (
                          <div key={`mood-${i}`} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-muted/20">
                            <MoodDot score={m.score} />
                            <div className="flex-1 min-w-0">
                              <span className="text-[11px] font-medium">{m.label || `Mood ${m.score}/10`}</span>
                              {m.note && <p className="text-[10px] text-muted-foreground truncate">{m.note}</p>}
                            </div>
                            <span className="text-[9px] text-muted-foreground">{formatTime(m.logged_at)}</span>
                          </div>
                        ))}

                        {/* Sessions for this day */}
                        {day.sessions.slice(0, 8).map(s => (
                          <button
                            key={s.id}
                            data-testid={`timeline-session-${s.id}`}
                            onClick={() => navigate(`/chat?id=${s.id}`)}
                            className="w-full flex items-start gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/30 transition-colors text-left"
                          >
                            <MessageCircle className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-medium truncate">{s.title}</p>
                              {s.last_message_preview && <p className="text-[10px] text-muted-foreground truncate">{s.last_message_preview}</p>}
                            </div>
                            <span className="text-[9px] text-muted-foreground flex-shrink-0 mt-0.5">{MODULE_LABELS[s.module] || s.module}</span>
                          </button>
                        ))}
                        {day.sessions.length > 8 && (
                          <p className="text-[10px] text-muted-foreground pl-2">+{day.sessions.length - 8} more sessions</p>
                        )}

                        {/* Saved sessions */}
                        {day.saved_sessions.map(s => (
                          <div key={s.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-amber-50/50 dark:bg-amber-950/10">
                            <Bookmark className="w-3 h-3 text-amber-500 flex-shrink-0" />
                            <span className="text-[11px] text-amber-700 dark:text-amber-400 truncate">{s.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
